import type {
    AstroConfig,
    HookParameters,
    ImageQualityPreset,
    ImageTransform,
} from 'astro'

type ImageFormat = 'image/webp'

export const imageSizes = [640, 1080, 1920] as const
export type ImageSizes = (typeof imageSizes)[number]

export type DevImageService = 'sharp' | (string & {})

type RemotePattern = {
    protocol?: 'http' | 'https'
    hostname: string
    port?: string
    pathname?: string
}

export type WlodevImageConfig = {
    /**
     * Supported image widths.
     */
    sizes: ImageSizes[]
    /**
     * Supported output image formats
     */
    formats?: ImageFormat[]
    /**
     * Allowed external domains that can use Image Optimization. Leave empty for only allowing the deployment domain to use Image Optimization.
     */
    domains: string[]
    /**
     * Allowed external patterns that can use Image Optimization. Similar to `domains` but provides more control with RegExp.
     */
    remotePatterns?: RemotePattern[]
}

export const getDefaultImageConfig = (
    astroImageConfig: AstroConfig['image']
): WlodevImageConfig => {
    return {
        sizes: [...imageSizes],
        formats: ['image/webp'],
        domains: astroImageConfig.domains ?? [],
        remotePatterns:
            (astroImageConfig.remotePatterns as WlodevImageConfig['remotePatterns']) ??
            [],
    }
}

export function isESMImportedImage(
    src: ImageMetadata | string
): src is ImageMetadata {
    return typeof src === 'object'
}

export const qualityTable: Record<ImageQualityPreset, number> = {
    low: 25,
    mid: 50,
    high: 70,
    max: 80,
}

export const getImageConfig = (
    enabled: boolean,
    imageConfig: WlodevImageConfig | undefined,
    astroImageConfig: AstroConfig['image'],
    devImageService: DevImageService,
    command: HookParameters<'astro:config:setup'>['command'],
    logger: HookParameters<'astro:config:setup'>['logger']
) => {
    let devService = '@wlodev/astro/adapter/sharp-dev-image-service'

    switch (devImageService) {
        case 'sharp':
            devService = '@wlodev/astro/adapter/sharp-dev-image-service'
            break
        default:
            if (typeof devImageService === 'string') {
                devService = devImageService
            } else {
                devService = '@wlodev/astro/adapter/sharp-dev-image-service'
            }
            break
    }

    if (enabled) {
        return {
            image: {
                service: {
                    entrypoint:
                        command === 'dev'
                            ? devService
                            : '@wlodev/astro/adapter/image-service',
                    config: imageConfig
                        ? imageConfig
                        : getDefaultImageConfig(astroImageConfig),
                },
            },
        }
    }

    return {}
}

export const sharedValidateOptions = (
    options: ImageTransform,
    serviceConfig: Record<string, any>,
    mode: 'development' | 'production'
) => {
    const wlodevImageOptions = serviceConfig as WlodevImageConfig

    if (
        mode === 'development' &&
        (!wlodevImageOptions.sizes || wlodevImageOptions.sizes.length === 0)
    ) {
        throw new Error(
            'Provide at least one image size for wlodev image optimization'
        )
    }

    const configuredWidths = wlodevImageOptions.sizes.sort((a, b) => a - b)
    if (!options.width) {
        const src = options.src
        if (isESMImportedImage(src)) {
            const nearestWidth = configuredWidths.reduce((prev, curr) => {
                return Math.abs(curr - src.width) < Math.abs(prev - src.width)
                    ? curr
                    : prev
            })

            // Use the image's base width to inform the `width` and `height` on the `img` tag
            options.inputtedWidth = src.width
            options.imageSize = nearestWidth
            options.width = nearestWidth
        } else {
            throw new Error(
                `Missing \`width\` parameter for remote image ${options.src}`
            )
        }
    } else {
        if (!configuredWidths.includes(options.width as ImageSizes)) {
            const nearestWidth = configuredWidths.reduce((prev, curr) => {
                return Math.abs(curr - options.width!) <
                    Math.abs(prev - options.width!)
                    ? curr
                    : prev
            })

            // Save the width the user asked for to inform the `width` and `height` on the `img` tag
            options.inputtedWidth = options.width
            options.imageSize = nearestWidth
            options.width = nearestWidth
        }
    }

    if (options.quality && typeof options.quality === 'string') {
        options.quality =
            options.quality in qualityTable
                ? qualityTable[options.quality]
                : undefined
    }

    if (!options.quality) {
        options.quality = qualityTable.max
    }

    // Generate srcset
    if (!options.widths && mode === 'production') {
        options.widths =
            wlodevImageOptions.sizes ?? (imageSizes as unknown as number[])
    }

    return options
}
